import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BlockIcon from '@mui/icons-material/Block';

import { isDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../utils/back-errors.util';
import type { BackError } from '../../utils/back-errors.util';
import { MessageList } from '../../components/MessageList';

import { useSyncThirdPartyToPennylaneMutation } from './hooks/useSyncThirdPartyToPennylaneMutation.hook';

interface Props {
  readonly thirdPartyId: Id;
  readonly accountCode: string;
  readonly accountLabel: string;
  readonly isEnabled: boolean;
  readonly activeId?: number | bigint | null;
  readonly invalidateQueryKey: readonly unknown[];
}

export const PennylaneAccountRow = ({
  thirdPartyId,
  accountCode,
  accountLabel,
  isEnabled,
  activeId,
  invalidateQueryKey,
}: Readonly<Props>) => {
  const isSynced = isDefined(activeId);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: sync, isPending } = useSyncThirdPartyToPennylaneMutation();

  const handleSync = useCallback(() => {
    sync(
      { thirdPartyId, userAccount: accountCode as never },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
          enqueueSnackbar('Synchronisation Pennylane effectuée.');
        },
        onError: (error: unknown) => {
          enqueueSnackbar(
            <MessageList messages={userFriendlyErrors(error as BackError)} />,
            {
              variant: 'error',
            },
          );
        },
      },
    );
  }, [
    accountCode,
    enqueueSnackbar,
    invalidateQueryKey,
    queryClient,
    sync,
    thirdPartyId,
  ]);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 2,
        opacity: isEnabled ? 1 : 0.5,
        py: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 'medium' }} variant="body1">
          {accountLabel}
        </Typography>
      </Box>

      {!isEnabled ? (
        <Chip
          color="default"
          icon={<BlockIcon />}
          label="Compte désactivé"
          size="small"
          variant="outlined"
        />
      ) : (
        <Chip
          color={isSynced ? 'success' : 'default'}
          icon={
            isSynced ? (
              <CheckCircleOutlinedIcon />
            ) : (
              <RadioButtonUncheckedIcon />
            )
          }
          label={isSynced ? `ID ${String(activeId)}` : 'Non synchronisé'}
          size="small"
          variant={isSynced ? 'filled' : 'outlined'}
        />
      )}

      <Button
        disabled={!isEnabled || isPending}
        loading={isPending}
        onClick={handleSync}
        size="small"
        startIcon={isSynced ? <SyncIcon /> : <AddIcon />}
        variant={isSynced ? 'outlined' : 'contained'}
      >
        {isSynced ? 'Mettre à jour' : 'Créer'}
      </Button>
    </Box>
  );
};
